import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import RootStore from '@App/stores/root-store'
import { action, computed, makeObservable, observable, observableRef } from 'mobx'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { SearchOptionType } from '@App/enum/search-option-type'
import { RequiredCriteria } from '@App/enum/required-criteria'
import { findPageOnLimitChange } from '@App/helpers/pagination/find-page-on-limit-change'
import { WorkSearch } from '@App/stores/search/models/work-search'
import { searchWorks } from '@App/api/routes/search'
import { generateSearchCriterionFromOption } from '@App/helpers/generate-search-criterion'
import { Works } from '@Entities/work/works'
import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import { v4 as uuidv4 } from 'uuid'
import { getClaimWorkAutoCompleteSuggestions } from '@App/helpers/search/get-claim-auto-complete-suggestions'
import { ClaimSearchFilter } from '@App/forms/filters/claim-search-filter'
import { mapCriteriaToObject } from '@App/helpers/map-criteria-to-object'

export const DEFAULT_CLAIM_PAGINATION = {
    page: 1,
    limit: 10,
}

const CLAIM_WORK_OPTIONS: Array<SelectOptionHide<WorkSearch>> = [
    {
        id: uuidv4(),
        value: 'combinedTitles',
        label: 'Work Title',
        isPrimary: true,
        autoCompleteFetcher: getClaimWorkAutoCompleteSuggestions,
        exactMatchOnly: true,
    },
    {
        id: uuidv4(),
        value: 'writerFullNames',
        label: 'Writer Name',
        exactMatchOnly: true,
    },
    { id: uuidv4(), value: 'iswc', label: 'ISWC', isPrimary: true },
    { id: uuidv4(), value: 'hfaSongCode', label: 'MLC Song Code', isPrimary: true },
    { id: uuidv4(), value: 'writerNameNumbers', label: 'Writer IPI' },
    {
        id: uuidv4(),
        value: 'originalPubNames',
        label: 'Publisher Name',
        exactMatchOnly: true,
    },
    { id: uuidv4(), value: 'originalPubIpiNameNumbers', label: 'Publisher IPI' },
    { id: uuidv4(), value: 'bothPublisherNumbers', label: 'MLC Publisher Number' },
]
export const DEFAULT_CLAIM_CRITERIA: Array<SearchCriterion<WorkSearch>> = [
    generateSearchCriterionFromOption(CLAIM_WORK_OPTIONS[0], true),
    generateSearchCriterionFromOption(CLAIM_WORK_OPTIONS[1], true),
]

export default class ClaimingToolSearchCriteriaStore extends SearchCriteriaStore<WorkSearch> {
    constructor(
        criteria: Array<SearchCriterion<WorkSearch>>,
        options: typeof CLAIM_WORK_OPTIONS,
        optionType: SearchOptionType,
        requiredCriteria: RequiredCriteria,
        maxCriteria: number
    ) {
        super(criteria, options, optionType, requiredCriteria, maxCriteria)
    }
}

export class ClaimingToolSearchStore {
    private rootStore: RootStore

    createClaimingToolSearchCriteriaStore(): SearchCriteriaStore<WorkSearch> {
        const maxCriteria = 5
        return new ClaimingToolSearchCriteriaStore(
            DEFAULT_CLAIM_CRITERIA,
            CLAIM_WORK_OPTIONS,
            SearchOptionType.filter,
            RequiredCriteria.some,
            maxCriteria
        )
    }

    private _unclaimedWorks: Works | undefined

    private _claimingToolSearchCriteriaStore: SearchCriteriaStore<WorkSearch>

    private _unclaimedWorksPaginationInfo: PaginationInfo | undefined

    private _claimFilter: ClaimSearchFilter | undefined

    private _claimPaginationBody: PaginationBody

    private _claimSearchQuery: WorkSearch | undefined

    /** Exact query key `_unclaimedWorks` was fetched under, so a remount only reuses it when it still matches. */
    private _lastResultsKey: unknown[] | undefined

    private _redirectingToCatalog = false

    constructor(rootStore: RootStore) {
        makeObservable<
            ClaimingToolSearchStore,
            | '_claimFilter'
            | '_claimSearchQuery'
            | '_claimPaginationBody'
            | '_lastResultsKey'
            | '_unclaimedWorks'
            | '_unclaimedWorksPaginationInfo'
            | '_claimingToolSearchCriteriaStore'
        >(this, {
            _claimFilter: observable,
            _claimSearchQuery: observable,
            _claimPaginationBody: observableRef,
            _lastResultsKey: observableRef,
            setClaimFilter: action,
            seedSearchFromUrl: action,
            setClaimSearchQuery: action,
            setClaimPaginationBody: action,
            setCurrentPage: action,
            onChangeLimit: action,
            applyUnclaimedWorks: action,
            resetClaimSearchQuery: action,
            setRedirectingToCatalog: action,
            claimSearchQuery: computed,
            _unclaimedWorks: observable,
            _unclaimedWorksPaginationInfo: observable,
            destroyUnclaimedWorks: action,
            destroyUnclaimedWorksPaginationInfo: action,
            _claimingToolSearchCriteriaStore: observable,
            resetSearchCriteriaStore: action,
            resetClaimSearchCriteriaIfNeeded: action,
        })

        this.rootStore = rootStore
        this.resetClaimSearchCriteriaIfNeeded()
    }

    public setCurrentPage(page: number) {
        this._claimPaginationBody = { ...this._claimPaginationBody, page }
    }

    get limit(): number {
        return this._claimPaginationBody.limit ?? DEFAULT_CLAIM_PAGINATION.limit
    }

    get claimPaginationBody(): PaginationBody {
        return this._claimPaginationBody
    }

    get claimFilter(): ClaimSearchFilter | undefined {
        return this._claimFilter
    }

    get lastResultsKey(): unknown[] | undefined {
        return this._lastResultsKey
    }

    onChangeLimit(newLimit: number) {
        let page = this._claimPaginationBody.page
        if (this._unclaimedWorks) {
            page = findPageOnLimitChange(this._claimPaginationBody, this._unclaimedWorks.count, newLimit, 1)
        }
        this._claimPaginationBody = { ...this._claimPaginationBody, page, limit: newLimit }
    }

    get searchCriteriaStore(): SearchCriteriaStore<WorkSearch> {
        return this._claimingToolSearchCriteriaStore
    }

    resetSearchCriteriaStore(): void {
        this._claimingToolSearchCriteriaStore = this.createClaimingToolSearchCriteriaStore()
    }

    /**
     * Starts a visit from a link's search terms and filters. Run it before the page first renders: it swaps
     * in a criteria store seeded from the URL, drops results from a different search, and — only if the
     * terms satisfy the form's own "ready to search" rule — makes them the search, so the first request
     * already carries them. (Without terms the tool never searches on load, so this isn't called.)
     */
    seedSearchFromUrl(criteria: Record<string, unknown>, filter?: ClaimSearchFilter): void {
        this.resetSearchCriteriaStore()
        this.searchCriteriaStore.applyCriteriaFromObject(criteria)
        this.destroyUnclaimedWorks()
        this.destroyUnclaimedWorksPaginationInfo()
        this._claimFilter = filter
        this._claimPaginationBody = { ...DEFAULT_CLAIM_PAGINATION }
        const rows = this.searchCriteriaStore.searchCriteria
        this._claimSearchQuery = this.searchCriteriaStore.isCriteriaSearchReady(rows)
            ? (mapCriteriaToObject(rows) as WorkSearch)
            : undefined
    }

    resetClaimSearchCriteriaIfNeeded(): void {
        if (!this.redirectingToCatalog) {
            this._claimSearchQuery = undefined
            this.resetSearchCriteriaStore()
            this.destroyUnclaimedWorks()
            this.destroyUnclaimedWorksPaginationInfo()
            this.resetClaimSearchQuery()
            this._claimPaginationBody = { ...DEFAULT_CLAIM_PAGINATION }
            this._lastResultsKey = undefined
        }
        this.setRedirectingToCatalog(false)
    }

    get redirectingToCatalog(): boolean {
        return this._redirectingToCatalog
    }

    setRedirectingToCatalog(value: boolean): void {
        this._redirectingToCatalog = value
    }

    setClaimFilter(filter?: ClaimSearchFilter | undefined) {
        this._claimFilter = filter
    }

    setClaimPaginationBody(pagination: PaginationBody) {
        this._claimPaginationBody = { ...pagination }
    }

    get claimSearchQuery(): WorkSearch | undefined {
        return this._claimSearchQuery
    }

    setClaimSearchQuery(claimSearchQuery: WorkSearch): void {
        this._claimSearchQuery = claimSearchQuery
    }

    resetClaimSearchQuery(): void {
        this._claimSearchQuery = undefined
    }

    /**
     * Performs the search request without touching any observable state, so it can be owned by an
     * external cache (TanStack Query). Errors propagate to the caller.
     */
    async fetchUnclaimedWorks(
        query: WorkSearch,
        filter: ClaimSearchFilter | undefined,
        pagination: PaginationBody
    ): Promise<Works> {
        const search: WorkSearch = {
            ...query,
            ...{ isOverclaimable: true },
            ...(!filter?.notClaimedByFilter && {
                notClaimedBy: this.rootStore.rightsholderStore.currentRightsholder?.hfaPublisherNumber,
            }),
            ...(filter?.matchedRecordingsFilter && {
                matchedRecordingsFilter: filter.matchedRecordingsFilter,
            }),
        }
        const apiResponse = await searchWorks(pagination, search)
        return { count: apiResponse.data.totalElements, works: apiResponse.data.content }
    }

    /** Stores results fetched elsewhere so components still reading the store stay in sync. */
    applyUnclaimedWorks(response: Works, pagination: PaginationBody, resultsKey: unknown[]): void {
        this._unclaimedWorks = response
        this._lastResultsKey = resultsKey
        if (pagination.limit && pagination.page) {
            this._unclaimedWorksPaginationInfo = calcNumPages(
                response.count,
                pagination.limit,
                pagination.page,
                pagination.refined
            )
        }
        const workIds = response.works.map((work) => work.id).filter((workId): workId is number => !!workId)
        this.rootStore.rightsholderStore.loadClaimedShares(workIds).then()
    }

    reportSearchError(error: unknown): void {
        this.rootStore.popupStore.setAlertFromError(error)
    }

    get unclaimedWorks(): Works | undefined {
        return this._unclaimedWorks
    }

    destroyUnclaimedWorks(): void {
        this._unclaimedWorks = undefined
    }

    get unclaimedWorksPaginationInfo(): PaginationInfo | undefined {
        return this._unclaimedWorksPaginationInfo
    }

    destroyUnclaimedWorksPaginationInfo(): void {
        this._unclaimedWorksPaginationInfo = undefined
    }
}
